/**
 * Helper Functions
 * Utility funkcije za FeedManager
 */

import { FlatField } from '../../../lib/xmlParser';
import { AUTO_DETECT_PATTERNS } from './fieldConfig';

// ============================================================================
// VALUE EXTRACTION
// ============================================================================

/**
 * Clean path from displayPath suffixes like [×N], [complex], etc.
 * Moved here to be available for getNestedValue
 */
function cleanPathSuffixes(path: string): string {
  return path.replace(/\s*\[×?\d*\]|\s*\[complex\]/g, '');
}

/**
 * Get nested value from object by path
 */
export function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return undefined;

  // Clean path from any displayPath suffixes
  const cleanPath = cleanPathSuffixes(path);
  const parts = cleanPath.split('/');
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) return undefined;

    if (Array.isArray(current)) {
      current = current[0]?.[part];
    } else {
      current = current[part];
    }
  }

  if (typeof current === 'object' && current !== null && !Array.isArray(current)) {
    const keys = Object.keys(current);
    if (keys.length === 0) return '';
    if (keys.length === 1 && keys[0] === '#text') return current['#text'];
  }

  return current;
}

/**
 * Extract text from complex value (object with attributes)
 */
export function extractTextValue(value: any): string {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value !== 'object') return String(value);

  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    const first = extractTextValue(value[0]);
    return value.length > 1 ? `${first} (+${value.length - 1})` : first;
  }

  // Object - try to get text content
  if ('#text' in value) return String(value['#text']);

  // Check for single text-like property
  const keys = Object.keys(value).filter(k => !k.startsWith('@'));
  if (keys.length === 1) {
    return extractTextValue(value[keys[0]]);
  }

  // Show attribute if exists
  const attrKeys = Object.keys(value).filter(k => k.startsWith('@'));
  if (attrKeys.length > 0) {
    return `{${attrKeys[0]}=${value[attrKeys[0]]}}`;
  }

  return `{${keys.length} props}`;
}

/**
 * Get sample value from record by path
 */
export function getSampleValue(
  sampleRecords: any[],
  sampleIndex: number,
  path: string,
  recordElement: string
): string {
  if (!sampleRecords?.[sampleIndex]) return '';

  const recordElementIndex = path.indexOf(recordElement + '/');
  if (recordElementIndex === -1) return '';

  const recordPath = path.substring(recordElementIndex + recordElement.length + 1);
  if (!recordPath) return '';

  const value = getNestedValue(sampleRecords[sampleIndex], recordPath);
  return extractTextValue(value).slice(0, 50);
}

// ============================================================================
// AUTO-DETECTION
// ============================================================================

/**
 * Auto-detect mappings based on field names
 */
export function autoDetectMappings(fields: FlatField[]): Record<string, string> {
  const auto: Record<string, string> = {};

  fields.forEach(field => {
    const fieldName = field.path.split('/').pop() || '';

    Object.entries(AUTO_DETECT_PATTERNS).forEach(([dbField, regexes]) => {
      if (!auto[dbField]) {
        for (const regex of regexes) {
          if (regex.test(fieldName)) {
            auto[dbField] = field.displayPath;
            break;
          }
        }
      }
    });
  });

  return auto;
}

// ============================================================================
// ARRAY HELPERS
// ============================================================================

/**
 * Get available keys from array object for keyValue configuration
 * Searches through all sample records to find array with objects
 */
export function getArrayObjectKeys(
  sampleRecords: any[],
  sampleIndex: number,
  fieldPath: string,
  recordElement: string
): string[] {
  if (!sampleRecords?.length) return [];

  // Clean path from record element prefix (path cleaning is done in getNestedValue)
  let cleanPath = fieldPath;
  if (cleanPath.startsWith(recordElement + '/')) {
    cleanPath = cleanPath.substring(recordElement.length + 1);
  }

  // First try the current sample
  if (sampleRecords[sampleIndex]) {
    const value = getNestedValue(sampleRecords[sampleIndex], cleanPath);
    if (Array.isArray(value) && value.length > 0) {
      const firstItem = value[0];
      if (typeof firstItem === 'object' && firstItem !== null) {
        return Object.keys(firstItem);
      }
    }
  }

  // If not found, search through all samples
  for (let i = 0; i < sampleRecords.length; i++) {
    if (i === sampleIndex) continue; // Skip already checked

    const value = getNestedValue(sampleRecords[i], cleanPath);
    if (Array.isArray(value) && value.length > 0) {
      const firstItem = value[0];
      if (typeof firstItem === 'object' && firstItem !== null) {
        return Object.keys(firstItem);
      }
    }
  }

  return [];
}

/**
 * Get array values for preview
 */
export function getArrayValues(
  sampleRecords: any[],
  sampleIndex: number,
  fieldPath: string,
  recordElement: string
): any[] {
  if (!sampleRecords?.[sampleIndex]) return [];

  // Clean path from record element prefix (path cleaning is done in getNestedValue)
  let cleanPath = fieldPath;
  if (cleanPath.startsWith(recordElement + '/')) {
    cleanPath = cleanPath.substring(recordElement.length + 1);
  }

  const value = getNestedValue(sampleRecords[sampleIndex], cleanPath);
  return Array.isArray(value) ? value : [value];
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

/**
 * Format preview value for keyValue fields
 */
export function formatKeyValuePreview(
  rawValue: any[],
  keyField: string,
  valueField: string
): string {
  if (!Array.isArray(rawValue)) return '-';

  const kvResult: Record<string, string> = {};
  rawValue.forEach((item: any) => {
    if (item && typeof item === 'object') {
      const key = item[keyField];
      const val = item[valueField];
      if (key) {
        const keyStr = typeof key === 'object' && '#text' in key ? key['#text'] : key;
        const valStr = typeof val === 'object' && '#text' in val ? val['#text'] : (val || '');
        kvResult[keyStr] = valStr;
      }
    }
  });

  const keys = Object.keys(kvResult);
  return keys.length > 0
    ? `{${keys.length} attr: ${keys.slice(0, 2).join(', ')}${keys.length > 2 ? '...' : ''}}`
    : '-';
}

// ============================================================================
// CLIPBOARD
// ============================================================================

/**
 * Copy path to clipboard
 */
export function copyPathToClipboard(path: string): string {
  const displayPath = path.replace(/^[^/]+\/[^/]+\//, '');
  navigator.clipboard.writeText(displayPath);
  return displayPath;
}
